import React from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@circuitry-ai/doc-data';
import { Icon } from '@iconify/react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'github-markdown-css/github-markdown.css';
import { v4 as uuidv4 } from 'uuid';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Input } from '@nextui-org/react';
import { useSaveAiWorkerUsageMutation, useGetAgentHisotryMutation } from '@circuitry-ai/doc-data';
import CtyPromptInput, { promptInputType, ErrorBoundary, CtyShowToast } from '@circuitry-ai/doc-ui';
import { ResizableSplitPanel } from '../../uxgenie/components/shared/ResizableSplitPanel';
import { GenieRenderingEngine } from '../engines';
import { useClaimsHostFunctions } from './useClaimsHostFunctions';
import { formatUTCDateTime } from '../../uxgenie/utils';
import { GenieTemplates, type TemplatePayload } from './GenieTemplates';
import { streamGenie, runGenie } from './genieService';

// Must be hoisted to module scope — never inside render
const CtyPromptInputWithErrorBoundary = ErrorBoundary(CtyPromptInput);

type MainTab = 'converse' | 'blueprints';
type PreviewTab = 'preview' | 'component' | 'config' | 'integration';

type TraceEntry = {
  step: number;
  type: string;
  label?: string;
  content?: string;
  tool?: string;
  duration_ms?: number;
};

type ChatMessage = {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  status?: 'thinking' | 'success' | 'error';
  startedAt?: number;
  finishedAt?: number;
  error?: string;
  mode?: 'reply' | 'output';
  trace?: TraceEntry[];
  traceDurationMs?: number;
  responseMeta?: {
    component: string;
    formConfig: Record<string, any> | null;
    toolUsed?: string | null;
    sampleData?: Record<string, any> | null;
  };
};

type RunResponse = {
  conversation_id?: string;
  message?: string | null;
  type?: string | null;
  tool_used?: string | null;
  trace?: TraceEntry[] | null;
  output?: unknown;
  sample_data?: string | null;
};

const UX_GENIE_COMPONENT_ONLY_TOOLS = new Set(['ux_genie', 'ux_genie_edit']);

const createId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const parseJsonIfPossible = (value: unknown) => {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return value; }
};

const normalizeToolUsed = (toolUsed?: string | null) =>
  toolUsed?.split('[')[0]?.trim() || '';

// Returns true only for tools that never produce a formConfig.
// Tools with a '[form]' qualifier (e.g. 'ux_genie[form]') always include
// formConfig and must not be treated as component-only.
const isUxGenieComponentOnlyTool = (toolUsed?: string | null) => {
  if (toolUsed?.includes('[form]')) return false;
  return UX_GENIE_COMPONENT_ONLY_TOOLS.has(normalizeToolUsed(toolUsed));
};

const parseRunOutput = (output: unknown, toolUsed?: string | null) => {
  const parsedOutput = parseJsonIfPossible(output);
  if (!parsedOutput || typeof parsedOutput !== 'object') {
    throw new Error('The response did not include a valid JSON payload in output.');
  }
  const result = parsedOutput as Record<string, unknown>;
  const formConfig = parseJsonIfPossible(result.formConfig);
  const component = result.component;

  if (formConfig == null) {
    if (!isUxGenieComponentOnlyTool(toolUsed)) {
      throw new Error('The response did not include formConfig.');
    }
  } else if (typeof formConfig !== 'object') {
    throw new Error('The response did not include formConfig.');
  }

  if (typeof component !== 'string' || !component.trim()) {
    throw new Error('The response did not include a React component string.');
  }

  return {
    component,
    formConfig: (formConfig as Record<string, any> | null) ?? null,
    rawOutput:
      typeof output === 'string'
        ? output
        : JSON.stringify(parsedOutput, null, 2),
  };
};

const GENIE_BASE_URL =
  (import.meta.env.CTY_GENIE_BASE_URL as string | undefined) ?? 'https://dev.genie.circuitry.ai';

const buildIntegrationSnippet = (prompt: string, conversationId: string) =>
  `POST ${GENIE_BASE_URL}/run
Content-Type: application/json

{
  "message": ${JSON.stringify(prompt || '')}
}

const response = await fetch('${GENIE_BASE_URL}/run', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ message: ${JSON.stringify(prompt || '')} })
});

const data = await response.json();
const parsedOutput = JSON.parse(data.output);
const formConfig = parsedOutput.formConfig ?? null;
const component = parsedOutput.component;
const conversationId = data.conversation_id;

window.FORM_CONFIG = formConfig;
window.FORM_RUNTIME_CONFIG = {};
window.formConfig = formConfig;

await fetch('${GENIE_BASE_URL}/run', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    conversation_id: ${JSON.stringify(conversationId || '<conversation_id>')},
    message: 'Add a field for dealer code in the Vehicle section'
  })
});
`;

// ---------------------------------------------------------------------------
// Markdown renderer (assistant messages)
// ---------------------------------------------------------------------------
const assistantMarkdownComponents: Components = {
  ul: ({ node, ...props }) => <ul className="list-disc pl-5" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-5" {...props} />,
  table: ({ node, children, ...props }) => (
    <table
      className="my-2 w-full border-collapse border border-default-300 table-fixed"
      style={{ tableLayout: 'fixed', width: '100%' }}
      {...props}
    >
      {children}
    </table>
  ),
  td: ({ node, children, ...props }) => (
    <td
      className="border border-default-300 px-3 py-2 text-left break-words"
      style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
      title={typeof children === 'string' ? children : String(children)}
      {...props}
    >
      {children}
    </td>
  ),
  th: ({ node, children, ...props }) => (
    <th
      className="border border-default-300 bg-default-100 px-3 py-2 text-left font-semibold break-words"
      scope="col"
      style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
      title={typeof children === 'string' ? children : String(children)}
      {...props}
    >
      {children}
    </th>
  ),
};

const AssistantMarkdown = ({
  content,
  className = '',
}: {
  content: string;
  className?: string;
}) => (
  <div
    className={`markdown-body overflow-x-auto text-[12px] leading-5 ${className}`}
    style={{
      color: 'inherit',
      backgroundColor: 'transparent',
      display: 'inline-block',
      maxWidth: '100%',
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
    }}
  >
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={assistantMarkdownComponents}>
      {content.replace(/\n/g, '  \n')}
    </ReactMarkdown>
  </div>
);

// ---------------------------------------------------------------------------
// Avatar helpers
// ---------------------------------------------------------------------------
const GenieAvatar = () => (
  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-2xl bg-[#FFF4E8]">
    <Icon icon=" " className="text-[#FF9F1C]" width={20} />
  </div>
);

const UserAvatar = ({ initials }: { initials: string }) => (
  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-2xl bg-[#EAECF0]">
    {initials ? (
      <span className="text-[11px] font-semibold text-default-600">{initials}</span>
    ) : (
      <Icon icon="mdi:account-outline" className="text-default-600" width={18} />
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------
const EmptyState = ({ title, description }: { title: string; description: string }) => (
  <div className="flex h-full min-h-[320px] flex-col items-center justify-center border border-dashed border-default-200 bg-content1 px-8 text-center">
    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F2F4F7] text-default-600">
      <Icon icon="mdi:creation-outline" width={26} />
    </div>
    <p className="text-[12px] font-semibold text-foreground">{title}</p>
    {description && (
      <p className="mt-2 max-w-xl text-[12px] leading-5 text-default-500">{description}</p>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// SmallGenie
// ---------------------------------------------------------------------------
export function SmallGenie() {
  // ------------------------------------------------------------------
  // Auth / user info
  // ------------------------------------------------------------------
  const authToken = useSelector((state: RootState) => state.auth.token);
  const { details: user } = useSelector((state: RootState) => state.auth.user);
  const userInitials = React.useMemo(() => {
    const name: string = (user as Record<string, unknown>)?.name as string ?? '';
    return name
      .split(' ')
      .map((part: string) => part[0] ?? '')
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, [user]);

  // ------------------------------------------------------------------
  // Tab state
  // ------------------------------------------------------------------
  const [mainTab, setMainTab] = React.useState<MainTab>('converse');

  // ------------------------------------------------------------------
  // Chat state
  // ------------------------------------------------------------------
  const [messages, setMessages] = React.useState<ChatMessage[]>([
    {
      id: 'intro',
      role: 'assistant',
      content: 'Hi! I am your Genie — describe what you want and I will generate a component for you.',
    },
  ]);
  const [conversationId, setConversationId] = React.useState('');
  const [formConfig, setFormConfig] = React.useState<Record<string, any> | null>(null);
  const [componentCode, setComponentCode] = React.useState('');
  const [toolUsed, setToolUsed] = React.useState<string | null>(null);
  const [sampleData, setSampleData] = React.useState<Record<string, any> | null>(null);
  const [toolOutput, setToolOutput] = React.useState<string | null>(null);
  const [previewTab, setPreviewTab] = React.useState<PreviewTab>('preview');
  const [expandedTraceIds, setExpandedTraceIds] = React.useState<Record<string, boolean>>({});
  const [lastPrompt, setLastPrompt] = React.useState('');
  const [liveThinkingContent, setLiveThinkingContent] = React.useState('');
  const scrollAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  const hostFunctions = useClaimsHostFunctions(formConfig);


  const hasPendingRequest = messages.some((m) => m.status === 'thinking');

  React.useEffect(() => {
    scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, previewTab]);

  const updateAssistantMessage = React.useCallback(
    (messageId: string, nextState: Partial<ChatMessage>) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, ...nextState } : m))
      );
    },
    []
  );

  const toggleTrace = React.useCallback((messageId: string) => {
    setExpandedTraceIds((prev) => ({ ...prev, [messageId]: !prev[messageId] }));
  }, []);

  const openPreviewForMessage = React.useCallback(
    (responseMeta: NonNullable<ChatMessage['responseMeta']>) => {
      setFormConfig(responseMeta.formConfig);
      setComponentCode(responseMeta.component);
      setToolUsed(responseMeta.toolUsed ?? null);
      setSampleData(responseMeta.sampleData ?? null);
      setPreviewTab('preview');
    },
    []
  );

  // ------------------------------------------------------------------
  // Templates → Chat: load a saved template into the preview
  // ------------------------------------------------------------------
  const handleOpenTemplate = React.useCallback((data: TemplatePayload) => {
    setComponentCode(data.component);
    setFormConfig(data.formConfig);
    setToolUsed(data.toolUsed);
    setSampleData(null);
    setToolOutput(null);
    setPreviewTab('preview');
    setMainTab('converse');
  }, []);

  // ------------------------------------------------------------------
  // Send / API call
  // ------------------------------------------------------------------

  /** Shared response-handling logic used by both streaming and non-streaming paths */
  const applyGenieResponse = React.useCallback(
    (data: RunResponse, assistantMessageId: string, collectedTrace: TraceEntry[]) => {
      const assistantMessage =
        data.message?.trim() ||
        (data.output ? 'Generated component ready.' : 'Reply ready.');

      const thinkingTrace = collectedTrace.filter((e) => e.type === 'thinking' && e.label);
      const traceDurationMs =
        [...collectedTrace].reverse().find((e) => typeof e.duration_ms === 'number')
          ?.duration_ms ?? undefined;

      if (data.conversation_id) setConversationId(data.conversation_id);

      const parsedSampleData = data.sample_data
        ? (parseJsonIfPossible(data.sample_data) as Record<string, any> | null)
        : null;

      if (data.output) {
        const parsed = parseRunOutput(data.output, data.tool_used);
        setFormConfig(parsed.formConfig);
        setComponentCode(parsed.component);
        setToolUsed(data.tool_used ?? null);
        setSampleData(parsedSampleData);
        setToolOutput(
          typeof data.output === 'string' ? data.output : JSON.stringify(data.output)
        );
        setPreviewTab('preview');
        if (thinkingTrace.length > 0) {
          setExpandedTraceIds((prev) => ({ ...prev, [assistantMessageId]: false }));
        }
        updateAssistantMessage(assistantMessageId, {
          status: 'success',
          finishedAt: Date.now(),
          content: assistantMessage,
          mode: 'output',
          trace: thinkingTrace,
          traceDurationMs,
          responseMeta: {
            component: parsed.component,
            formConfig: parsed.formConfig,
            toolUsed: data.tool_used ?? null,
            sampleData: parsedSampleData,
          },
          error: undefined,
        });
      } else {
        setFormConfig(null);
        setComponentCode('');
        setToolUsed(data.tool_used ?? null);
        setSampleData(null);
        setPreviewTab('preview');
        if (thinkingTrace.length > 0) {
          setExpandedTraceIds((prev) => ({ ...prev, [assistantMessageId]: false }));
        }
        updateAssistantMessage(assistantMessageId, {
          status: 'success',
          finishedAt: Date.now(),
          content: assistantMessage,
          mode: 'reply',
          trace: thinkingTrace,
          traceDurationMs,
          responseMeta: undefined,
          error: undefined,
        });
      }
    },
    [
      updateAssistantMessage,
      setConversationId,
      setFormConfig,
      setComponentCode,
      setToolUsed,
      setSampleData,
      setToolOutput,
      setPreviewTab,
      setExpandedTraceIds,
    ]
  );

  const handleSendClick = React.useCallback(
    async (userInput: string) => {
      const trimmedPrompt = userInput.trim();
      if (!trimmedPrompt || hasPendingRequest) return;

      // Cancel any in-flight request
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const userMessageId = createId();
      const assistantMessageId = createId();

      setLastPrompt(trimmedPrompt);
      setLiveThinkingContent('');
      setMessages((prev) => [
        ...prev,
        { id: userMessageId, role: 'user', content: trimmedPrompt },
        {
          id: assistantMessageId,
          role: 'assistant',
          content: 'Generating component output.',
          status: 'thinking',
          startedAt: Date.now(),
        },
      ]);
      setFormConfig(null);
      setComponentCode('');
      setToolUsed(null);
      setSampleData(null);
      setToolOutput(null);

      const geniePayload = {
        message: trimmedPrompt,
        ...(conversationId ? { conversation_id: conversationId } : {}),
        ...(authToken ? { token: authToken } : {}),
      };

      // Accumulate trace entries during streaming so applyGenieResponse can use them
      const collectedTrace: TraceEntry[] = [];

      try {
        await streamGenie(
          geniePayload,
          {
            onTraceEntry: (entry) => {
              collectedTrace.push(entry as TraceEntry);
              // Show latest thinking step label live in the bubble
              if (entry.type === 'thinking' && entry.label) {
                setLiveThinkingContent(entry.label);
              }
            },
            onFinalResponse: (data) => {
              setLiveThinkingContent('');
              applyGenieResponse(data as RunResponse, assistantMessageId, collectedTrace);
            },
            onError: async (streamError) => {
              // Stream dropped mid-flight (e.g. ERR_QUIC_PROTOCOL_ERROR on large
              // payloads). Fall back to /run which uses HTTP/1.1 and handles large
              // responses reliably.
              setLiveThinkingContent('');
              try {
                const data = await runGenie(geniePayload);
                const fallbackTrace = (data.trace ?? []) as TraceEntry[];
                applyGenieResponse(data as unknown as RunResponse, assistantMessageId, fallbackTrace);
              } catch (runError) {
                updateAssistantMessage(assistantMessageId, {
                  status: 'error',
                  finishedAt: Date.now(),
                  content: 'The request failed.',
                  error:
                    runError instanceof Error ? runError.message : streamError.message,
                });
              }
            },
          },
          abortController.signal
        );
      } catch (error) {
        setLiveThinkingContent('');
        updateAssistantMessage(assistantMessageId, {
          status: 'error',
          finishedAt: Date.now(),
          content: 'The request failed.',
          error:
            error instanceof Error ? error.message : 'Unexpected error while calling Genie.',
        });
      }
    },
    [conversationId, authToken, hasPendingRequest, updateAssistantMessage, applyGenieResponse]
  );

  // ------------------------------------------------------------------
  // Save
  // ------------------------------------------------------------------
  const [isSaveModalOpen, setIsSaveModalOpen] = React.useState(false);
  const [templateName, setTemplateName] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveAiWorkerUsage] = useSaveAiWorkerUsageMutation();
  const [getGenieHistory] = useGetAgentHisotryMutation();
  const SMALLGENIE_WORKER_ID = ''; // TODO: replace with real worker id once registered

  const getNextTemplateNumber = async (): Promise<number> => {
    try {
      const response = await getGenieHistory({
        pagination: { page: 1, per_page: 100 },
        filters: [{ aiworker_type: 'genie_logs', condition: 'contains' }],
      }).unwrap();
      type HistoryRecord = { output_data?: { action_name?: string }; action_output?: { action_name?: string } };
      const res = response as Record<string, unknown>;
      let records: HistoryRecord[] = [];
      if (Array.isArray(res?.aiworkers_conversations)) records = res.aiworkers_conversations as HistoryRecord[];
      else if (Array.isArray((res?.data as Record<string, unknown>)?.aiworkers_conversations)) records = ((res.data as Record<string, unknown>).aiworkers_conversations) as HistoryRecord[];
      else if (Array.isArray(res?.data)) records = res.data as HistoryRecord[];
      else if (Array.isArray(response)) records = response as HistoryRecord[];

      let maxNumber = 0;
      const pattern = /^Genie Template\s*(\d+)?$/i;
      records.forEach((r) => {
        const name = r?.output_data?.action_name || r?.action_output?.action_name || '';
        const match = name.match(pattern);
        if (match) {
          const num = match[1] ? parseInt(match[1], 10) : 0;
          if (num > maxNumber) maxNumber = num;
        }
      });
      return maxNumber + 1;
    } catch {
      return 1;
    }
  };

  const handleSave = React.useCallback(() => {
    if (!componentCode) return;
    setTemplateName('');
    setIsSaveModalOpen(true);
  }, [componentCode]);

  const confirmSave = React.useCallback(async () => {
    if (!componentCode || isSaving) return;
    setIsSaving(true);
    try {
      let nameToUse = templateName.trim();
      if (!nameToUse) {
        const nextNumber = await getNextTemplateNumber();
        nameToUse = `Genie Template ${nextNumber}`;
      }
      const usageId = uuidv4();
      const currentTime = new Date();
      const now = formatUTCDateTime(currentTime);
      await saveAiWorkerUsage({
        aiworker_id: SMALLGENIE_WORKER_ID,
        payLoad: {
          aiworker_id: SMALLGENIE_WORKER_ID,
          aiworker_usage_id: usageId,
          aiworker_type: 'genie_logs',
          used_at: now,
          status: 'succeeded',
          input_data: { user_input: nameToUse, original_data: toolOutput ?? '' },
          output_data: {
            generated_code: componentCode,
            action_name: nameToUse,
            saved_at: now,
          },
        },
      }).unwrap();
      setIsSaveModalOpen(false);
      CtyShowToast('success', `Template "${nameToUse}" saved successfully!`);
    } catch (error) {
      CtyShowToast('error', `Failed to save template: ${(error as Error).message}`);
    } finally {
      setIsSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentCode, isSaving, templateName, toolOutput, saveAiWorkerUsage]);

  // ------------------------------------------------------------------
  // Message rendering  (chatadvisor-style bubbles with avatars)
  // ------------------------------------------------------------------
  const renderAssistantMessage = (message: ChatMessage) => {
    // Welcome / plain message (no status)
    if (!message.status) {
      return (
        <div className="flex items-start gap-3">
          <GenieAvatar />
          <div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-[#F9FAFB] px-4 py-2.5">
            <AssistantMarkdown content={message.content} className="text-default-600" />
          </div>
        </div>
      );
    }

    // Thinking
    if (message.status === 'thinking') {
      return (
        <div className="flex items-start gap-3">
          <GenieAvatar />
          <div className="max-w-[80%] rounded-[14px] border border-[#FECACA] bg-[#F9FAFB] px-4 py-3">
            <div className="flex items-center gap-2 text-foreground">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#FF9F1C] [animation-delay:-0.2s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#FF9F1C] [animation-delay:-0.1s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#FF9F1C]" />
              <span className="text-[12px] font-medium">Thinking…</span>
            </div>
            {liveThinkingContent && (
              <p className="mt-2 line-clamp-3 text-[11px] leading-4 text-default-500">
                {liveThinkingContent}
              </p>
            )}
          </div>
        </div>
      );
    }

    // Completed / error
    const thoughtDurationSeconds = message.traceDurationMs
      ? Math.max(1, Math.round(message.traceDurationMs / 1000))
      : message.startedAt && message.finishedAt
        ? Math.max(1, Math.round((message.finishedAt - message.startedAt) / 1000))
        : 0;
    const hasTrace = Boolean(message.trace?.length);
    const isTraceExpanded = Boolean(expandedTraceIds[message.id]);
    const responseMeta = message.responseMeta;

    return (
      <div className="flex items-start gap-3">
        <GenieAvatar />
        <div className="w-full max-w-[85%] overflow-hidden rounded-2xl rounded-tl-sm border border-default-200 bg-content1 shadow-sm">
          {/* Trace (collapsible) */}
          {hasTrace && (
            <div className="border-b border-default-200 px-4 py-3">
              <button
                className="flex w-full items-center justify-between rounded-[10px] border border-[#FECACA] bg-[#F9FAFB] px-3 py-2 text-left"
                onClick={() => toggleTrace(message.id)}
                type="button"
              >
                <div className="flex items-center gap-2 text-foreground">
                  <Icon
                    icon={isTraceExpanded ? 'mdi:chevron-down' : 'mdi:chevron-right'}
                    width={16}
                  />
                  <span className="text-[12px] font-medium">
                    Thinking{thoughtDurationSeconds > 0 ? ` (${thoughtDurationSeconds}s)` : ''}
                  </span>
                </div>
              </button>
              {isTraceExpanded && (
                <ul className="mt-3 space-y-2 pl-4 text-[12px] leading-6 text-default-500">
                  {message.trace?.map((entry) => (
                    <li key={`${message.id}-${entry.step}`} className="list-disc">
                      {entry.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Body */}
          <div className="px-4 py-3">
            {message.status === 'error' && (
              <p className="mb-1 text-[12px] font-semibold text-foreground">Run failed</p>
            )}
            {message.mode === 'output' && message.status === 'success' && (
              <p className="mb-1 text-[12px] font-semibold text-foreground">Generated component</p>
            )}
            <AssistantMarkdown content={message.content} className="text-default-500" />
          </div>

          {/* Open preview action */}
          {message.status === 'success' && responseMeta && (
            <div className="border-t border-default-200 px-4 py-3">
              <button
                className="rounded-full border border-default-200 px-3 py-1.5 text-[12px] font-semibold text-default-600 hover:bg-[#F9FAFB] transition-colors"
                onClick={() => openPreviewForMessage(responseMeta)}
                type="button"
              >
                Open preview
              </button>
            </div>
          )}

          {/* Error detail */}
          {message.status === 'error' && message.error && (
            <div className="border-t border-[#FEE4E2] px-4 py-3 text-[12px] text-danger">
              {message.error}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ------------------------------------------------------------------
  // Chat panel (left side of split)
  // ------------------------------------------------------------------
  const chatPanel = (
    <section className="flex h-full min-h-0 flex-col bg-[#F8FAFC]">
      {/* Message list */}
      <div className="custom-scrollbar flex-1 overflow-y-auto overflow-x-hidden px-4 py-5">
        <div className="mx-auto flex w-full max-w-[640px] flex-col gap-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex items-end gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
            >
              {/* Assistant: avatar left */}
              {message.role === 'assistant' && renderAssistantMessage(message)}

              {/* User: bubble right, then avatar */}
              {message.role === 'user' && (
                <>
                  <div className="max-w-[75%] rounded-2xl rounded-br-sm border border-default-200 bg-content1 px-4 py-2.5 shadow-sm">
                    <p className="text-[12px] leading-5 text-default-600">{message.content}</p>
                  </div>
                  <UserAvatar initials={userInitials} />
                </>
              )}
            </div>
          ))}
          <div ref={scrollAnchorRef} />
        </div>
      </div>

      {/* Input — CtyPromptInput matching FormGenieSplitPanel style */}
      <div className="flex-shrink-0 border-t border-default-200 bg-content1 px-4 pb-4 pt-3">
        <CtyPromptInputWithErrorBoundary
          elementType={promptInputType.TEXTAREA}
          id="genie-chatinput"
          classNames={{
            inputWrapper:
              '!bg-content1 shadow-none border border-default-200 rounded-[10px] px-4 pt-3 pb-2',
            innerWrapper: 'relative',
            input: 'text-[12px] p-0',
          }}
          radius="none"
          variant="flat"
          handleSendClick={(text: string) => void handleSendClick(text)}
          placeholder="Describe what you want, or paste your JSON schema…"
          enableSpeak={false}
          enableSendBtn={true}
          enableMention={false}
          enableActionMention={false}
          enableFile={false}
          enableImage={false}
          enableTranslation={false}
          enableSearch={false}
          clearInputOnSend={true}
        />
      </div>
    </section>
  );

  // ------------------------------------------------------------------
  // Preview panel (right side of split)
  // ------------------------------------------------------------------
  const canRenderPreview = Boolean(componentCode);
  const codeBlockClassName =
    'custom-scrollbar h-full w-full min-w-0 overflow-auto whitespace-pre-wrap break-words rounded-[24px] bg-[#0F172A] p-4 text-xs leading-6 text-[#E2E8F0] lg:p-6';

  const previewPanel = (
    <section className="flex h-full min-h-0 flex-col overflow-hidden bg-[#F8FAFC]">
      {/* Preview tab bar + save button */}
      <div className="flex items-center justify-between border-b border-default-200 bg-content1 px-4 py-2">
        <div className="flex gap-1">
          {(['preview', 'component', 'config', 'integration'] as PreviewTab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setPreviewTab(tab)}
              className={`rounded-md px-3 py-1.5 text-[11px] font-medium transition-colors ${previewTab === tab
                ? 'bg-[#F2F4F7] text-foreground'
                : 'text-default-500 hover:text-default-600'
                }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        <button
          type="button"
          disabled={!canRenderPreview || isSaving}
          onClick={handleSave}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors ${!canRenderPreview || isSaving
            ? 'cursor-not-allowed border border-default-200 bg-[#F9FAFB] text-default-500'
            : 'border border-default-200 bg-content1 text-default-600 hover:bg-[#F9FAFB]'
            }`}
        >
          <Icon
            icon={isSaving ? 'mdi:loading' : 'mdi:content-save-outline'}
            width={14}
            className={isSaving ? 'animate-spin' : ''}
          />
          {isSaving ? 'Saving…' : 'Save template'}
        </button>
      </div>

      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
        {previewTab === 'preview' &&
          (canRenderPreview ? (
            <div className="h-full min-w-0 overflow-hidden rounded-[24px]">
              <GenieRenderingEngine
                output={{ component: componentCode, formConfig }}
                toolUsed={toolUsed}
                hostFunctions={hostFunctions}
                data={sampleData}
              />
            </div>
          ) : (
            <EmptyState title="Live preview will render here" description="" />
          ))}

        {previewTab === 'component' &&
          (componentCode ? (
            <pre className={codeBlockClassName}>
              <code>{componentCode}</code>
            </pre>
          ) : (
            <EmptyState
              title="React component output"
              description="This tab shows the raw component string returned by the Genie endpoint."
            />
          ))}

        {previewTab === 'config' &&
          (formConfig ? (
            <pre className={codeBlockClassName}>
              <code>{JSON.stringify(formConfig, null, 2)}</code>
            </pre>
          ) : (
            <EmptyState
              title="formConfig JSON"
              description={
                isUxGenieComponentOnlyTool(toolUsed)
                  ? 'The current response is component-only — no formConfig was returned.'
                  : 'Once a run succeeds, the parsed formConfig payload will appear here.'
              }
            />
          ))}

        {previewTab === 'integration' && (
          <pre className={codeBlockClassName}>
            <code>{buildIntegrationSnippet(lastPrompt, conversationId)}</code>
          </pre>
        )}
      </div>
    </section>
  );

  // ------------------------------------------------------------------
  // Root layout
  // ------------------------------------------------------------------
  return (
    <>
    {/* Save Modal */}
    <Modal isOpen={isSaveModalOpen} onClose={() => setIsSaveModalOpen(false)} size="md">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <span>Save Template</span>
          <span className="text-sm font-normal text-default-500">Enter a name for this template</span>
        </ModalHeader>
        <ModalBody>
          <Input
            label="Template Name"
            placeholder="e.g., Genie Template 1"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            variant="bordered"
            description={templateName.trim() ? '' : 'If left empty, will be named "Genie Template [number]"'}
            onKeyDown={(e) => { if (e.key === 'Enter') void confirmSave(); }}
          />
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="rounded-lg px-4 py-2 text-sm text-default-600 hover:bg-[#F9FAFB]"
            onClick={() => setIsSaveModalOpen(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isSaving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#101828] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            onClick={() => void confirmSave()}
          >
            {isSaving && <Icon icon="mdi:loading" width={14} className="animate-spin" />}
            {templateName.trim() ? 'Save' : 'Save with Auto-name'}
          </button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    <div className="flex h-full min-h-0 flex-col bg-[#F8FAFC]">
      {/* Top tab bar — Chat | Templates */}
      <nav className="flex flex-shrink-0 border-b border-default-200 bg-content1 px-4">
        {(
          [
            { id: 'converse', label: 'Converse', icon: '' },
            { id: 'blueprints', label: 'Blueprints', icon: 'mdi:view-grid-outline' },
          ] as { id: MainTab; label: string; icon: string }[]
        ).map(({ id, label, icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setMainTab(id)}
            className={`-mb-px mr-6 flex items-center gap-1.5 border-b-2 py-3 text-[13px] font-medium transition-colors ${mainTab === id
              ? 'border-[#101828] text-foreground'
              : 'border-transparent text-default-500 hover:text-default-600'
              }`}
          >
            <Icon icon={icon} width={15} />
            {label}
          </button>
        ))}
      </nav>

      {/* Chat tab */}
      {mainTab === 'converse' && (
        <div className="min-h-0 flex-1 overflow-hidden border border-default-200 bg-content1">
          <ResizableSplitPanel
            leftPanel={chatPanel}
            rightPanel={previewPanel}
            defaultLeftWidth={38}
            minLeftWidth={28}
            minRightWidth={32}
          />
        </div>
      )}

      {/* Templates tab */}
      {mainTab === 'blueprints' && (
        <div className="min-h-0 flex-1 overflow-hidden bg-content1">
          <GenieTemplates onOpenTemplate={handleOpenTemplate} />
        </div>
      )}
    </div>
    </>
  );
}

export default SmallGenie;
